import React, { useState, useEffect } from 'react';
import {
    Building2, Car, Calendar, DollarSign, TrendingUp, Star, Plus, Edit, Trash2,
    BarChart3, Settings, Users, MessageSquare, FileText, Bell, X, Check,
    Clock, MapPin, Phone, Mail, Filter, Search, Download, Upload, Eye,
    ChevronDown, ChevronUp, AlertCircle, CheckCircle, XCircle, Info,
    ArrowUpRight, ArrowDownRight, Activity, Package, CreditCard, Ban,
    RefreshCw, Send, Image as ImageIcon, Calendar as CalendarIcon
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
    ReservationsView,
    CalendarView,
    ClientsView,
    FinancesView,
    ProfileView,
    NotificationsView
} from './AgencyDashboardViews';
import CreateVehicleModal from './modals/CreateVehicleModal';
import EditVehicleModal from './modals/EditVehicleModal';
import ReservationDetailsModal from './modals/ReservationDetailsModal';
import ClientProfileModal from './modals/ClientProfileModal';

// Composants UI réutilisables
const StatCard = ({ title, value, icon: Icon, trend, color = "blue", subtitle, onClick }) => (
    <div
        onClick={onClick}
        className={`bg-gradient-to-br from-${color}-500/20 to-${color}-600/10 
              border border-${color}-400/30 rounded-2xl p-6 
              hover:shadow-xl transition-all duration-300 hover:scale-105 
              ${onClick ? 'cursor-pointer' : ''}`}
    >
        <div className="flex items-center justify-between mb-4">
            <div className={`p-3 bg-${color}-500/20 rounded-xl`}>
                <Icon className={`h-6 w-6 text-${color}-400`} />
            </div>
            {trend !== undefined && (
                <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full 
                     ${trend > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                    {Math.abs(trend)}%
                </div>
            )}
        </div>
        <div className="space-y-1">
            <p className="text-3xl font-bold text-white">{value}</p>
            <p className="text-slate-300 text-sm font-medium">{title}</p>
            {subtitle && <p className="text-slate-400 text-xs">{subtitle}</p>}
        </div>
    </div>
);

const TabButton = ({ id, label, icon: Icon, active, onClick, badge }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 
            whitespace-nowrap flex-shrink-0 relative
    ${active
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
            }`}
    >
        <Icon className="h-4 w-4" />
        <span className="hidden sm:inline">{label}</span>
        {badge > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                {badge}
            </span>
        )}
    </button>
);

// Composant principal du tableau de bord agence amélioré
const EnhancedAgencyDashboard = ({ api }) => {
    const [agency, setAgency] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [clients, setClients] = useState([]);
    const [stats, setStats] = useState({});
    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('dashboard');
    const [showCreateVehicle, setShowCreateVehicle] = useState(false);
    const [showCreateAgency, setShowCreateAgency] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [filters, setFilters] = useState({
        vehicleStatus: 'all',
        reservationStatus: 'all',
        dateRange: '30days',
        searchQuery: ''
    });
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        fetchAgencyData();
    }, []);

    const fetchAgencyData = async () => {
        try {
            setLoading(true);

            const [agencyRes, statsRes, analyticsRes, vehiclesRes, reservationsRes, clientsRes, notificationsRes] = await Promise.all([
                api.get('/api/agencies/my').catch(() => null),
                api.get('/api/agencies/my/stats').catch(() => ({ data: {} })),
                api.get('/api/agencies/my/analytics').catch(() => ({ data: {} })),
                api.get('/api/agencies/my/vehicles').catch(() => ({ data: { vehicles: [] } })),
                api.get('/api/agencies/my/reservations').catch(() => ({ data: { reservations: [] } })),
                api.get('/api/agencies/my/clients').catch(() => ({ data: { clients: [] } })),
                api.get('/api/agencies/my/notifications').catch(() => ({ data: { notifications: [] } }))
            ]);

            if (!agencyRes) {
                setAgency(null);
                return;
            }

            setAgency(agencyRes.data);
            setStats(statsRes.data || {});
            setAnalytics(analyticsRes.data || {});
            setVehicles(vehiclesRes.data.vehicles || []);
            setReservations(reservationsRes.data.reservations || []);
            setClients(clientsRes.data.clients || []);
            setNotifications(notificationsRes.data.notifications || []);
            setUnreadCount(notificationsRes.data.notifications?.filter(n => !n.read).length || 0);

        } catch (error) {
            console.error('Error fetching agency data:', error);
            setAgency(null);
        } finally {
            setLoading(false);
        }
    };

    // Données pour les graphiques
    const revenueData = analytics.monthly_revenue || [
        { month: 'Jan', revenue: 45000 },
        { month: 'Fév', revenue: 52000 },
        { month: 'Mar', revenue: 48000 },
        { month: 'Avr', revenue: 61000 },
        { month: 'Mai', revenue: 55000 },
        { month: 'Juin', revenue: 67000 }
    ];

    const occupancyData = analytics.vehicle_occupancy || [
        { name: 'Disponible', value: 45, color: '#10b981' },
        { name: 'Loué', value: 35, color: '#3b82f6' },
        { name: 'Maintenance', value: 20, color: '#f59e0b' }
    ];

    const reservationsByStatus = analytics.reservations_by_status || [
        { status: 'Confirmée', count: 45 },
        { status: 'En attente', count: 12 },
        { status: 'Active', count: 23 },
        { status: 'Terminée', count: 156 }
    ];

    // Filtrer les données
    const filteredVehicles = vehicles.filter(v => {
        if (filters.vehicleStatus !== 'all' && v.status !== filters.vehicleStatus) return false;
        if (filters.searchQuery && !`${v.make} ${v.model}`.toLowerCase().includes(filters.searchQuery.toLowerCase())) return false;
        return true;
    });

    const filteredReservations = reservations.filter(r => {
        if (filters.reservationStatus !== 'all' && r.status !== filters.reservationStatus) return false;
        return true;
    });

    // Actions
    const updateReservationStatus = async (reservationId, newStatus) => {
        try {
            await api.put(`/api/agencies/my/reservations/${reservationId}/status`, { status: newStatus });
            await fetchAgencyData();
            return { success: true };
        } catch (error) {
            return { success: false, error: error.response?.data?.detail || 'Erreur' };
        }
    };

    const deleteVehicle = async (vehicleId) => {
        try {
            await api.delete(`/api/agencies/my/vehicles/${vehicleId}`);
            await fetchAgencyData();
            return { success: true };
        } catch (error) {
            return { success: false, error: error.response?.data?.detail || 'Erreur' };
        }
    };

    const updateVehicle = async (vehicleId, data) => {
        try {
            await api.put(`/api/agencies/my/vehicles/${vehicleId}`, data);
            await fetchAgencyData();
            return { success: true };
        } catch (error) {
            return { success: false, error: error.response?.data?.detail || 'Erreur' };
        }
    };

    // Composants UI réutilisables
    const StatCard = ({ title, value, icon: Icon, trend, color = "blue", subtitle, onClick }) => (
        <div
            onClick={onClick}
            className={`bg-gradient-to-br from-${color}-500/20 to-${color}-600/10 
                  border border-${color}-400/30 rounded-2xl p-6 
                  hover:shadow-xl transition-all duration-300 hover:scale-105 
                  ${onClick ? 'cursor-pointer' : ''}`}
        >
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-${color}-500/20 rounded-xl`}>
                    <Icon className={`h-6 w-6 text-${color}-400`} />
                </div>
                {trend !== undefined && (
                    <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full 
                         ${trend > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                        {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        {Math.abs(trend)}%
                    </div>
                )}
            </div>
            <div className="space-y-1">
                <p className="text-3xl font-bold text-white">{value}</p>
                <p className="text-slate-300 text-sm font-medium">{title}</p>
                {subtitle && <p className="text-slate-400 text-xs">{subtitle}</p>}
            </div>
        </div>
    );

    const TabButton = ({ id, label, icon: Icon, active, onClick, badge }) => (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 
                whitespace-nowrap flex-shrink-0 relative
        ${active
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                    : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                }`}
        >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
            {badge > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {badge}
                </span>
            )}
        </button>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center">
                <div className="text-center space-y-4">
                    <div className="animate-spin h-16 w-16 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="text-white text-xl">Chargement de votre agence...</p>
                </div>
            </div>
        );
    }

    if (!agency) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4">
                <div className="text-center space-y-8 max-w-2xl">
                    <div className="space-y-4">
                        <Building2 className="h-24 w-24 text-slate-500 mx-auto" />
                        <h2 className="text-3xl font-bold text-white">Créez votre agence</h2>
                        <p className="text-slate-300 text-lg leading-relaxed">
                            Vous n'avez pas encore d'agence. Créez votre agence de location pour commencer à générer des revenus.
                        </p>
                    </div>

                    <div className="bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                            <div className="text-center">
                                <div className="p-4 bg-emerald-500/20 rounded-xl mb-3 mx-auto w-fit">
                                    <Car className="h-8 w-8 text-emerald-400" />
                                </div>
                                <h3 className="text-white font-semibold mb-2">Gérez votre flotte</h3>
                                <p className="text-slate-400 text-sm">Ajoutez et gérez vos véhicules facilement</p>
                            </div>

                            <div className="text-center">
                                <div className="p-4 bg-blue-500/20 rounded-xl mb-3 mx-auto w-fit">
                                    <Calendar className="h-8 w-8 text-blue-400" />
                                </div>
                                <h3 className="text-white font-semibold mb-2">Réservations automatiques</h3>
                                <p className="text-slate-400 text-sm">Recevez des réservations de clients</p>
                            </div>

                            <div className="text-center">
                                <div className="p-4 bg-purple-500/20 rounded-xl mb-3 mx-auto w-fit">
                                    <DollarSign className="h-8 w-8 text-purple-400" />
                                </div>
                                <h3 className="text-white font-semibold mb-2">Générez des revenus</h3>
                                <p className="text-slate-400 text-sm">Monetisez votre flotte efficacement</p>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowCreateAgency(true)}
                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 
                       text-white font-semibold py-4 rounded-xl shadow-xl transition-all duration-300 
                       hover:scale-105 flex items-center justify-center gap-2"
                        >
                            <Plus className="h-5 w-5" />
                            Créer mon agence
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
            {/* Header avec informations agence */}
            <div className="bg-gradient-to-r from-slate-800/95 to-indigo-800/95 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="py-6 space-y-6">

                        {/* En-tête agence */}
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl blur opacity-75"></div>
                                    <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-2xl">
                                        <Building2 className="h-8 w-8 text-white" />
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-3xl font-bold text-white">{agency.name}</h1>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold border
                      ${agency.status === 'active'
                                                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30'
                                                : 'bg-amber-500/20 text-amber-400 border-amber-400/30'
                                            }`}>
                                            {agency.status === 'active' ? '✅ Active' : '⏳ En attente'}
                                        </span>
                                    </div>
                                    <p className="text-indigo-200 text-sm">{agency.city} • {vehicles.length} véhicules</p>
                                    <div className="flex items-center gap-1 mt-1">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} className={`h-3 w-3 text-amber-400 ${i < Math.floor(agency.avg_rating || 0) ? 'fill-current' : ''}`} />
                                        ))}
                                        <span className="text-amber-300 text-sm font-medium ml-1">
                                            {agency.avg_rating || 0} ({agency.total_reviews || 0} avis)
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <button
                                    onClick={() => setShowCreateVehicle(true)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl 
                           font-medium transition-all duration-200 hover:scale-105 shadow-lg
                           flex items-center gap-2"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span className="hidden sm:inline">Ajouter véhicule</span>
                                    <span className="sm:hidden">Véhicule</span>
                                </button>

                                <button
                                    onClick={() => setActiveView('notifications')}
                                    className="relative bg-slate-700 hover:bg-slate-600 text-white px-4 py-3 rounded-xl 
                           font-medium transition-all duration-200 hover:scale-105"
                                >
                                    <Bell className="h-5 w-5" />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Navigation par onglets */}
                        <div className="flex space-x-1 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                            <TabButton id="dashboard" label="Tableau de bord" icon={BarChart3} active={activeView === 'dashboard'} onClick={() => setActiveView('dashboard')} />
                            <TabButton id="vehicles" label="Ma flotte" icon={Car} active={activeView === 'vehicles'} onClick={() => setActiveView('vehicles')} />
                            <TabButton id="reservations" label="Réservations" icon={Calendar} active={activeView === 'reservations'} onClick={() => setActiveView('reservations')} badge={reservations.filter(r => r.status === 'pending').length} />
                            <TabButton id="calendar" label="Calendrier" icon={CalendarIcon} active={activeView === 'calendar'} onClick={() => setActiveView('calendar')} />
                            <TabButton id="clients" label="Clients" icon={Users} active={activeView === 'clients'} onClick={() => setActiveView('clients')} />
                            <TabButton id="finances" label="Finances" icon={DollarSign} active={activeView === 'finances'} onClick={() => setActiveView('finances')} />
                            <TabButton id="profile" label="Profil" icon={Settings} active={activeView === 'profile'} onClick={() => setActiveView('profile')} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenu principal */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Vue Dashboard */}
                {activeView === 'dashboard' && (
                    <DashboardView
                        stats={stats}
                        analytics={analytics}
                        revenueData={revenueData}
                        occupancyData={occupancyData}
                        reservationsByStatus={reservationsByStatus}
                        vehicles={vehicles}
                        reservations={reservations}
                        setActiveView={setActiveView}
                    />
                )}

                {/* Vue Véhicules */}
                {activeView === 'vehicles' && (
                    <VehiclesView
                        vehicles={filteredVehicles}
                        filters={filters}
                        setFilters={setFilters}
                        setShowCreateVehicle={setShowCreateVehicle}
                        setSelectedVehicle={setSelectedVehicle}
                        deleteVehicle={deleteVehicle}
                        updateVehicle={updateVehicle}
                    />
                )}

                {/* Vue Réservations */}
                {activeView === 'reservations' && (
                    <ReservationsView
                        reservations={filteredReservations}
                        filters={filters}
                        setFilters={setFilters}
                        setSelectedReservation={setSelectedReservation}
                        updateReservationStatus={updateReservationStatus}
                    />
                )}

                {/* Vue Calendrier */}
                {activeView === 'calendar' && (
                    <CalendarView
                        reservations={reservations}
                        vehicles={vehicles}
                    />
                )}

                {/* Vue Clients */}
                {activeView === 'clients' && (
                    <ClientsView
                        clients={clients}
                        setSelectedClient={setSelectedClient}
                    />
                )}

                {/* Vue Finances */}
                {activeView === 'finances' && (
                    <FinancesView
                        stats={stats}
                        analytics={analytics}
                        revenueData={revenueData}
                        reservations={reservations}
                    />
                )}

                {/* Vue Profil */}
                {activeView === 'profile' && (
                    <ProfileView
                        agency={agency}
                        fetchAgencyData={fetchAgencyData}
                    />
                )}

                {/* Vue Notifications */}
                {activeView === 'notifications' && (
                    <NotificationsView
                        notifications={notifications}
                        setUnreadCount={setUnreadCount}
                    />
                )}
            </div>

            {/* Modals */}
            <CreateVehicleModal
                isOpen={showCreateVehicle}
                onClose={() => setShowCreateVehicle(false)}
                onSuccess={fetchAgencyData}
            />

            <EditVehicleModal
                isOpen={!!selectedVehicle}
                onClose={() => setSelectedVehicle(null)}
                onSuccess={fetchAgencyData}
                vehicle={selectedVehicle}
            />

            <ReservationDetailsModal
                isOpen={!!selectedReservation}
                onClose={() => setSelectedReservation(null)}
                reservation={selectedReservation}
                onStatusUpdate={fetchAgencyData}
            />

            <ClientProfileModal
                isOpen={!!selectedClient}
                onClose={() => setSelectedClient(null)}
                client={selectedClient}
            />
        </div>
    );
};

// Vue Dashboard Analytique
const DashboardView = ({ stats, analytics, revenueData, occupancyData, reservationsByStatus, vehicles, reservations, setActiveView }) => (
    <div className="space-y-8">
        {/* KPIs principaux */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
                title="Revenus ce mois"
                value={`${Math.round((stats.monthly_revenue || 0) / 1000)}K DZD`}
                icon={DollarSign}
                color="purple"
                trend={15}
                subtitle="Revenus générés"
            />
            <StatCard
                title="Réservations actives"
                value={stats.active_reservations || 0}
                icon={Calendar}
                color="emerald"
                subtitle="En cours"
                onClick={() => setActiveView('reservations')}
            />
            <StatCard
                title="Véhicules total"
                value={stats.total_vehicles || 0}
                icon={Car}
                color="blue"
                trend={8}
                subtitle="Dans votre flotte"
                onClick={() => setActiveView('vehicles')}
            />
            <StatCard
                title="Taux d'occupation"
                value={`${stats.occupancy_rate || 0}%`}
                icon={Activity}
                color="amber"
                trend={-3}
                subtitle="Utilisation flotte"
            />
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Graphique de revenus */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-white">Évolution des revenus</h3>
                        <p className="text-slate-300 text-sm">6 derniers mois</p>
                    </div>
                    <Download className="h-5 w-5 text-slate-400 cursor-pointer hover:text-white transition-colors" />
                </div>

                <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={revenueData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                            labelStyle={{ color: '#f1f5f9' }}
                        />
                        <Line type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', r: 5 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Graphique d'occupation */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-white">Occupation de la flotte</h3>
                        <p className="text-slate-300 text-sm">État actuel</p>
                    </div>
                </div>

                <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                        <Pie
                            data={occupancyData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {occupancyData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* Activité récente */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Réservations récentes */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/20 rounded-lg">
                            <Calendar className="h-5 w-5 text-emerald-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Réservations récentes</h3>
                            <p className="text-slate-300 text-sm">Dernières demandes</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setActiveView('reservations')}
                        className="text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
                    >
                        Voir tout →
                    </button>
                </div>

                <div className="space-y-3">
                    {reservations.slice(0, 5).map(reservation => (
                        <div key={reservation.id} className="bg-slate-700/30 rounded-xl p-4 hover:bg-slate-700/50 transition-colors">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h4 className="text-white font-medium">{reservation.client_name}</h4>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium
                      ${reservation.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                                                reservation.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                                    'bg-blue-500/20 text-blue-400'
                                            }`}>
                                            {reservation.status === 'confirmed' ? 'Confirmée' :
                                                reservation.status === 'pending' ? 'En attente' : 'Active'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-slate-300">
                                        <span>{reservation.vehicle}</span>
                                        <span>•</span>
                                        <span>{new Date(reservation.pickup_date).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-emerald-400 font-bold">{reservation.amount?.toLocaleString()} DZD</p>
                                </div>
                            </div>
                        </div>
                    ))}

                    {reservations.length === 0 && (
                        <div className="text-center py-8 text-slate-400">
                            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>Aucune réservation récente</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Top véhicules */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                            <Star className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Véhicules populaires</h3>
                            <p className="text-slate-300 text-sm">Meilleures performances</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setActiveView('vehicles')}
                        className="text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors"
                    >
                        Gérer →
                    </button>
                </div>

                <div className="space-y-4">
                    {vehicles.slice(0, 5).map((vehicle, index) => (
                        <div key={vehicle.id} className="flex items-center gap-4 p-3 bg-slate-700/30 rounded-xl">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm
                ${index === 0 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                                    index === 1 ? 'bg-gradient-to-r from-slate-400 to-slate-500' :
                                        'bg-gradient-to-r from-amber-600 to-amber-500'}`}>
                                {index + 1}
                            </div>
                            <div className="flex-1">
                                <p className="text-white font-medium text-sm">{vehicle.make} {vehicle.model}</p>
                                <div className="flex items-center gap-3 mt-1">
                                    <span className="text-emerald-400 text-xs font-bold">{vehicle.daily_rate?.toLocaleString()} DZD/j</span>
                                    <div className="flex items-center gap-1">
                                        <Star className="h-3 w-3 text-amber-400 fill-current" />
                                        <span className="text-amber-300 text-xs">{vehicle.rating || 0}</span>
                                    </div>
                                    <span className="text-slate-400 text-xs">{vehicle.bookings_count || 0} rés.</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Alertes et notifications */}
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-400/30 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-500/20 rounded-xl">
                    <AlertCircle className="h-6 w-6 text-amber-400" />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">Alertes importantes</h3>
                    <div className="space-y-2">
                        <p className="text-slate-300 text-sm">• {reservations.filter(r => r.status === 'pending').length} réservations en attente de confirmation</p>
                        <p className="text-slate-300 text-sm">• {vehicles.filter(v => v.status === 'maintenance').length} véhicules en maintenance</p>
                        <p className="text-slate-300 text-sm">• {stats.pending_payments || 0} paiements en attente</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// Vue Véhicules
const VehiclesView = ({ vehicles, filters, setFilters, setShowCreateVehicle, setSelectedVehicle, deleteVehicle, updateVehicle }) => (
    <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
                <h2 className="text-2xl font-bold text-white">Ma Flotte</h2>
                <p className="text-slate-300">Gérer vos véhicules et leur disponibilité</p>
            </div>

            <button
                onClick={() => setShowCreateVehicle(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl 
                 font-medium transition-all duration-200 hover:scale-105 shadow-lg
                 flex items-center gap-2"
            >
                <Plus className="h-4 w-4" />
                Ajouter un véhicule
            </button>
        </div>

        {/* Filtres */}
        <div className="bg-slate-800/50 rounded-xl p-4 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Rechercher un véhicule..."
                        value={filters.searchQuery}
                        onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                        className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-lg pl-10 pr-4 py-2"
                    />
                </div>
            </div>

            <select
                value={filters.vehicleStatus}
                onChange={(e) => setFilters(prev => ({ ...prev, vehicleStatus: e.target.value }))}
                className="bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-4 py-2"
            >
                <option value="all">Tous les statuts</option>
                <option value="available">Disponible</option>
                <option value="rented">Loué</option>
                <option value="maintenance">Maintenance</option>
            </select>
        </div>

        {/* Liste des véhicules */}
        <div className="space-y-4">
            {vehicles.map(vehicle => (
                <div key={vehicle.id} className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm 
                        border border-slate-600/50 rounded-2xl p-6 
                        hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-3">
                                <h3 className="text-white font-semibold text-lg">{vehicle.make} {vehicle.model} {vehicle.year}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium border
                  ${vehicle.status === 'available'
                                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30'
                                        : vehicle.status === 'rented'
                                            ? 'bg-blue-500/20 text-blue-400 border-blue-400/30'
                                            : 'bg-red-500/20 text-red-400 border-red-400/30'
                                    }`}>
                                    {vehicle.status === 'available' ? '✅ Disponible' :
                                        vehicle.status === 'rented' ? '🚗 Loué' : '⚠️ Maintenance'}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                <div className="flex items-center gap-2 text-slate-300">
                                    <Car className="h-4 w-4 text-purple-400" />
                                    <span>{vehicle.category}</span>
                                </div>
                                <div className="flex items-center gap-2 text-slate-300">
                                    <DollarSign className="h-4 w-4 text-emerald-400" />
                                    <span className="font-medium text-emerald-300">{vehicle.daily_rate?.toLocaleString()} DZD/jour</span>
                                </div>
                                <div className="flex items-center gap-2 text-slate-300">
                                    <Star className="h-4 w-4 text-amber-400" />
                                    <span>{vehicle.rating || 0} ({vehicle.bookings_count || 0} avis)</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                            <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg 
                               transition-all duration-200 hover:scale-105 flex items-center gap-2 text-sm font-medium">
                                <CalendarIcon className="h-4 w-4" />
                                <span className="hidden sm:inline">Planning</span>
                            </button>
                            <button
                                onClick={() => setSelectedVehicle(vehicle)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg 
                         transition-all duration-200 hover:scale-105 flex items-center gap-2 text-sm font-medium"
                            >
                                <Edit className="h-4 w-4" />
                                <span className="hidden sm:inline">Modifier</span>
                            </button>
                            <button
                                onClick={async () => {
                                    if (window.confirm('Supprimer ce véhicule ?')) {
                                        await deleteVehicle(vehicle.id);
                                    }
                                }}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg 
                         transition-all duration-200 hover:scale-105 flex items-center gap-2 text-sm font-medium"
                            >
                                <Trash2 className="h-4 w-4" />
                                <span className="hidden sm:inline">Supprimer</span>
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            {vehicles.length === 0 && (
                <div className="text-center py-16 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                    <Car className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                    <h3 className="text-white text-lg font-semibold mb-2">Aucun véhicule</h3>
                    <p className="text-slate-400 mb-6">Ajoutez votre premier véhicule pour commencer</p>
                    <button
                        onClick={() => setShowCreateVehicle(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl 
                     font-medium transition-all duration-200 hover:scale-105"
                    >
                        Ajouter un véhicule
                    </button>
                </div>
            )}
        </div>
    </div>
);

export default EnhancedAgencyDashboard;
